import { BiX } from 'react-icons/bi'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import { SquircleAdapter } from '@/components/SquircleAdapter'
import { useCakeMakerState } from '@/store/useCakeMaker'
import { Button } from '@/components/Button'
import { useModal } from '@/hooks'

import { formatMoney } from '@/utils'

import './styles.scss'

interface StoreOrdersModalProps {
  isOpen: boolean
  onClose: () => void
}

export function StoreOrdersModal({ isOpen, onClose }: StoreOrdersModalProps) {
  const { storeOrders } = useCakeMakerState()
  const { modalRef } = useModal({ isOpen, onClose })

  return (
    <>
      <div
        className="modal-overlay"
        onClick={onClose}
        data-open={isOpen}
        aria-hidden
      />

      <SquircleAdapter
        ref={modalRef}
        cornerRadius={0}
        topLeftCornerRadius={24}
        topRightCornerRadius={24}
        bottomLeftCornerRadius={0}
        bottomRightCornerRadius={0}
        className="modal"
        data-open={isOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="store-orders-modal-title"
        tabIndex={-1}
      >
        <div className="modal__header">
          <Button
            className="modal__back-button"
            text="Fechar"
            variant="primary"
            leftRender={<BiX aria-hidden />}
            onClick={onClose}
            aria-label="Fechar histórico de pedidos"
          />
        </div>

        <div className="modal__content modal__content-store-orders">
          <div className="modal__content-store-orders__heading">
            <h4 id="store-orders-modal-title">Histórico de pedidos</h4>
            <p>
              Esse histórico é salvo no seu navegador e será perdido caso você
              limpe o cache ou cookies.
            </p>
          </div>

          <div className="modal__content-store-orders__orders">
            {storeOrders.map((order) => (
              <div
                className="modal__content-store-orders__orders__order"
                key={order.code}
              >
                <h4>{order.code}</h4>

                <div className="modal__content-store-orders__orders__order__items">
                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Data de entrega
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order.date}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Peso
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order.cake?.weight?.value}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Formato
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order.cake?.shape?.name}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Tema
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order.cake?.theme || '-'}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Massa
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order.cake?.batter?.name || '-'}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Recheio(s)
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order.cake?.fillings
                        ?.map((filling) => filling.name)
                        .join(', ') || '-'}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Cobertura
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order.cake?.topping?.name || '-'}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Adicional(is)
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order.cake?.additional
                        ?.map((additional) => additional.name)
                        .join(', ') || '-'}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Docinhos
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order.candies
                        ?.filter((candy) => candy.quantity > 0)
                        ?.map((candy) => `${candy.name} (${candy.quantity})`)
                        .join(', ') || '-'}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label">
                      Entrega
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value">
                      {order?.delivery?.zipCode || '-'}
                    </p>
                  </div>

                  <div className="modal__content-store-orders__orders__order__item">
                    <p className="modal__content-store-orders__orders__order__item__label stronger">
                      Total
                    </p>
                    <p className="modal__content-store-orders__orders__order__item__value stronger">
                      {formatMoney(order.price)}
                    </p>
                  </div>
                </div>

                <span>
                  Pedido por {order.customer.split(' ')[0]} em{' '}
                  {format(order.orderedAt!, 'dd MMM yyyy', {
                    locale: ptBR,
                  })}
                </span>
              </div>
            ))}
          </div>
        </div>
      </SquircleAdapter>
    </>
  )
}
